const throttle = require('../helpers/throttle.js');

const sessionkey = 'infinite-scroll';

/**
 * @param { HTML } wrapper - HTML element for infinite scroll funcitonality
 * @param { int } offsetHeight - Offset when bottom is reached, higher == earlier api call
 */
const infiniteScroll = (wrapper, offsetHeight) => {

	const items = wrapper.querySelector('.js-items-insert');
	const loading = wrapper.querySelector('.js-items-loading');
	const url = wrapper.getAttribute('data-infinitescroll-api');

	let allItemsFetched = false;
	let isFetching = false;
	let nextPage = 1;
	let lastCurrentPage;

	const pagesessionkey = sessionkey + window.location.pathname;

	/**
	 * Fetch Products for infinitely scrollable Products list
	 * based on automatic nextPage or initialNextPage.
	 * Prevents unneeded calls with allItemsFetched, isFetching and debounce.
	 * @param {string} [initialNextPage] 
	 * @param {function} [initialCb] - Optional initial callback to execute after either success or failure
	 * @returns 
	 */
	const fetchProducts = (initialNextPage, initialCb) => {
		// value of `page` in querystring can be either a number or a comma-separated list of numbers, 
		// ie `1` to get items for page 1 or `1,2,3` to get items for page 1, 2 and 3 in one call.
		const endpoint = `${url}${url.includes('?') ? '&' : '?'}page=${initialNextPage || nextPage}`;

		// Don't continue if all items are fetched or fetching is in progress
		if (allItemsFetched === true || isFetching === true) {
			return;
		}

		const bottomOfItemsReached = items.getBoundingClientRect().bottom <= window.innerHeight + parseInt(offsetHeight);

		// If loading element exists, add active class to visualize loading
		if (loading) {
			loading.classList.add('is-active');
		}

		// Don't continue if bottom of items hasn't been reached yet,
		// except if fetchProducts was called initially, in which case we
		// want to fetch despite the position of the user
		if (!initialNextPage && !bottomOfItemsReached) {
			return;
		}

		// Set isFetching to true to prevent fetchProducts
		// from getting called while in progress
		isFetching = true;

		// Start GET call based on methods defined in networking.js
		window.GET(endpoint).then(data => {
			try {
				const json = JSON.parse(data.response);

				if (json.Success && json.HTML) {
					items.innerHTML += json.HTML;

					// Save currentPage so that we can store it below in session
					lastCurrentPage = json.CurrentPage;

					// If all product pages are fetched, stop firing the api call
					allItemsFetched = json.TotalPages === json.CurrentPage;
					// Automatically set nextPage based on response's CurrentPage
					nextPage = json.CurrentPage + 1;

					// If loading element exists, now remove the acive class 
					// to visualize loading is done
					if (loading) {
						loading.classList.remove('is-active');
					}

					// Function fetchProduct is now allowed to be called again
					isFetching = false;

					// Possible for users to scroll too fast, so check again if bottom is reached
					if (items.getBoundingClientRect().bottom <= window.innerHeight + parseInt(offsetHeight)) {
						fetchProducts();
					}
				}
				// Call initial callback with success
				if (initialCb) initialCb(true);
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error(e, data);
				// Call initial callback with error
				if (initialCb) initialCb(false);
			}
		})
		.catch(e => {
			console.error(e);
			// Call initial callback with error
			if (initialCb) initialCb(false);
		});
	};

	// Store the lastCurrentPage and the current scrollPosition
	// so that we can navigate back to it if the user revisits
	// this page during their session
	items.addEventListener('click', event => {
		// Only save if the user clicks within the items on an element
		// that either is an anchor or is inside an anchor
		// and if lastCurrentPage is set
		if (lastCurrentPage && event.target.closest('a')) {
			// Setting is cleared after session. This way we don't have
			// to consider how far back we should store their position
			// and when to clear it
			sessionStorage.setItem(
				pagesessionkey,
				JSON.stringify({
					scrollPos: window.scrollY,
					lastCurrentPage
				})
			);
		}
	});

	// Limit api calls
	const throttledScroll = throttle(() => fetchProducts(null), 200);
	window.addEventListener('scroll', throttledScroll);

	// Check if we need to load a specific page and scroll to a specific position.
	// Based on the user clicking on/in an anchor in `items` during this session.
	let session = sessionStorage.getItem(pagesessionkey);
	if (session) {
		session = JSON.parse(session);

		nextPage = session.lastCurrentPage + 1;
		const initialNextPage = [];
		for (let i = 1; i <= session.lastCurrentPage; i += 1) {
			initialNextPage.push(i);
		}
		fetchProducts(initialNextPage.join(','), (success) => {
			if (success) {
				// Once products are fetched and inserted in the html,
				// load to same position user was before.
				window.scrollTo(0, session.scrollPos);
				// Remove session item since we don't need
				// to do this again if the user refreshes or
				// navigates away without clicking on an item
				sessionStorage.removeItem(pagesessionkey);
			}
		});
	} else {
		// If there is no session, run throttledScroll (fetchProducts) once
		// to get initial products if they can be loaded into view.
		throttledScroll();
	}
}

const init = () => {
	const $wrappers = document.querySelectorAll('.js-items-onscroll');

	$wrappers.forEach(wrapper => {
		const attr = parseInt(wrapper.getAttribute('data-infinitescroll-offset')); // Higher == Earlier API call
		const offsetHeight = isNaN(attr) ? 400 : attr; // check if offset is a number, otherwise fallback on 400

		infiniteScroll(wrapper, offsetHeight);
	})
}

module.exports = init;
